import React, { useState } from 'react';
import { Container, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import CountdownTimer from './CountdownTimer';
import { generateICS, downloadICSFile, generateCalendarLinks, detectDevice, CEC_2026_EVENT } from './calendarUtils';
import './styles.css';

const Section = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  // Función para manejar la selección de calendario
  const handleCalendarSelection = (type) => {
    const device = detectDevice();
    const calendarLinks = generateCalendarLinks(CEC_2026_EVENT);

    switch (type) {
      case 'google':
        window.open(calendarLinks.google, '_blank');
        setAlertMessage(t('Abriendo Google Calendar...'));
        break;
      
      case 'outlook':
        window.open(calendarLinks.outlook, '_blank');
        setAlertMessage(t('Abriendo Outlook...'));
        break;
      
      case 'office365':
        window.open(calendarLinks.office365, '_blank');
        setAlertMessage(t('Abriendo Office 365...'));
        break;
      
      case 'yahoo':
        window.open(calendarLinks.yahoo, '_blank');
        setAlertMessage(t('Abriendo Yahoo Calendar...'));
        break;
      
      case 'apple':
      case 'ics':
        const icsContent = generateICS(CEC_2026_EVENT);
        downloadICSFile(icsContent, 'CEC_2026_Guatemala.ics');
        
        if (device.isIOS) {
          setAlertMessage(t('Archivo descargado. Ábrelo con la app Calendario de Apple.'));
        } else if (device.isMac) {
          setAlertMessage(t('Archivo descargado. Haz doble clic para agregarlo a Calendario.'));
        } else {
          setAlertMessage(t('Archivo .ics descargado. Ábrelo con tu aplicación de calendario.'));
        }
        break;
      
      default:
        break;
    }

    setAlertType('info');
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
    setCalendarModalOpen(false);
  };

  // Función principal para guardar la fecha (detecta automáticamente)
  const handleSaveDate = () => {
    const device = detectDevice();
    
    // Si es móvil, mostrar opciones
    if (device.isMobile) {
      setCalendarModalOpen(true);
    } else {
      // En desktop, mostrar dropdown con opciones
      setCalendarModalOpen(true);
    }
  };

  // Función para manejar el envío del formulario
  const handleSubmitNewsletter = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!email || !name) {
      setAlertMessage(t('Por favor completa todos los campos requeridos.'));
      setAlertType('danger');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      return;
    }

    try {
      // Enviar datos al backend
      const response = await fetch('http://localhost:5000/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name, 
          email, 
          organization 
        })
      });

      const data = await response.json();

      if (data.success) {
        setAlertMessage(t(data.message || `¡Gracias ${name}! Te mantendremos informado sobre el CEC 2026.`));
        setAlertType('success');
        setShowAlert(true);
        
        // Limpiar formulario
        setName('');
        setEmail('');
        setOrganization('');
        
        setTimeout(() => {
          setModalOpen(false);
          setShowAlert(false);
        }, 3000);
      } else {
        setAlertMessage(t(data.message || 'Error al procesar la suscripción'));
        setAlertType('danger');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    } catch (error) {
      console.error('Error:', error);
      setAlertMessage(t('Error de conexión. Por favor intenta más tarde.'));
      setAlertType('danger');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  return (
    <>
      <section className="bg-home min-vh-100" id="home">
        <div className="home-center">
          <div className="home-desc-center" style={{ paddingBottom: '60px' }}>
            <Container>
              <Row className="justify-content-center">
                <Col lg="1"> </Col>
                <Col>
                  <div className="text-white text-center bg-dark-transparent p-4 mt-5">
                    <h1 className="home-title mb-0">
                      {t("III CONGRESO ESPACIAL CENTROAMERICANO")}
                    </h1>
                    <h3 className="mt-4 mb-0 mx-auto">
                      {t("16 al 18 de septiembre de 2026")}
                    </h3>
                    <h3 className="mb-0 mx-auto">
                      {t("Universidad del Valle de Guatemala (UVG)")}
                    </h3>
                    <h3 className="mb-0 mx-auto">
                      {t("Ciudad de Guatemala, Guatemala")}
                    </h3>

                    <p className="mt-4 text-light text-justify px-3">
                      {t(
                        "El III Congreso Espacial Centroamericano (CEC 2026) se llevará a cabo nuevamente en la Ciudad de Guatemala. Este evento reunirá a expertos, investigadores, estudiantes y entusiastas del sector espacial de toda la región para compartir conocimientos, experiencias y visiones sobre el futuro del desarrollo espacial en Centroamérica y Latinoamérica."
                      )}
                    </p>

                    {/* Botones CTA principales */}
                    <div className="mt-4 d-flex flex-column align-items-center gap-3 px-3">
                      <a
                        href="/registration"
                        className="btn btn-lg py-3"
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          fontWeight: "700",
                          fontSize: "1.1rem",
                          letterSpacing: "0.5px",
                          borderRadius: "10px",
                          border: "none",
                          boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                          transition: "all 0.3s ease",
                          width: "100%",
                          maxWidth: "380px",
                          height: "60px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.boxShadow = "0 10px 30px rgba(102, 126, 234, 0.5)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.4)";
                        }}
                      >
                        🎟️ {t("¡Regístrate aquí!")}
                      </a>
                    </div>

                    {/* Contador integrado */}
                    <div className="mt-5">
                      <CountdownTimer />
                    </div>

                    {/* Alertas */}
                    {showAlert && (
                      <Alert color={alertType} className="mt-3 mx-auto" style={{ maxWidth: '500px' }}>
                        {alertMessage}
                      </Alert>
                    )}

                    {/* Botones secundarios */}
                    <div className="mt-4 d-flex flex-column align-items-center gap-3 px-3">
                      <button 
                        className="btn btn-lg"
                        onClick={handleSaveDate}
                        style={{
                          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          color: "white",
                          fontWeight: "700",
                          fontSize: "1.1rem",
                          letterSpacing: "0.5px",
                          borderRadius: "10px",
                          border: "none",
                          boxShadow: "0 6px 20px rgba(17, 153, 142, 0.4)",
                          transition: "all 0.3s ease",
                          width: "100%",
                          maxWidth: "380px",
                          height: "60px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.boxShadow = "0 10px 30px rgba(17, 153, 142, 0.5)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(17, 153, 142, 0.4)";
                        }}
                      >
                        📅 {t("Guardar la fecha")}
                      </button>
                      <button 
                        className="btn btn-lg"
                        onClick={toggleModal}
                        style={{
                          background: "linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)",
                          color: "white",
                          fontWeight: "700",
                          fontSize: "1.1rem",
                          letterSpacing: "0.5px",
                          borderRadius: "10px",
                          border: "none",
                          boxShadow: "0 6px 20px rgba(252, 74, 26, 0.4)",
                          transition: "all 0.3s ease",
                          width: "100%",
                          maxWidth: "380px",
                          height: "60px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.boxShadow = "0 10px 30px rgba(252, 74, 26, 0.5)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(252, 74, 26, 0.4)";
                        }}
                      >
                        📧 {t("Mantenerme informado")}
                      </button>
                    </div>

                  </div>
                </Col>
                <Col lg="1"> </Col>
              </Row>
            </Container>
          </div>
        </div>
      </section>

      {/* Modal para el formulario de newsletter */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {t("Mantente informado sobre el CEC 2026")}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmitNewsletter}>
            <FormGroup>
              <Label for="name">
                {t("Nombre")} <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="name"
                id="name"
                placeholder={t("Tu nombre completo")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="email">
                {t("Correo electrónico")} <span className="text-danger">*</span>
              </Label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder={t("tu@email.com")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="organization">
                {t("Organización / Institución")}
              </Label>
              <Input
                type="text"
                name="organization"
                id="organization"
                placeholder={t("Universidad, empresa, etc. (opcional)")}
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </FormGroup>
            <div className="text-muted small">
              <p>{t("* Campos requeridos")}</p>
              <p>{t("No compartiremos tu información con terceros.")}</p>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            {t("Cancelar")}
          </Button>
          <Button color="primary" onClick={handleSubmitNewsletter}>
            {t("Suscribirme")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal para selección de calendario */}
      <Modal isOpen={calendarModalOpen} toggle={() => setCalendarModalOpen(false)} centered>
        <ModalHeader toggle={() => setCalendarModalOpen(false)}>
          {t("Agregar al Calendario")}
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <h5 className="mb-4">{t("Selecciona tu calendario preferido:")}</h5>
            
            <div className="d-grid gap-2">
              <Button 
                color="primary" 
                size="lg" 
                onClick={() => handleCalendarSelection('google')}
                className="d-flex align-items-center justify-content-start px-4"
              >
                <span className="me-3">📅</span>
                Google Calendar
              </Button>
              
              <Button 
                color="info" 
                size="lg" 
                onClick={() => handleCalendarSelection('outlook')}
                className="d-flex align-items-center justify-content-start px-4"
              >
                <span className="me-3">📘</span>
                Outlook.com
              </Button>
              
              <Button 
                color="primary" 
                size="lg" 
                onClick={() => handleCalendarSelection('office365')}
                className="d-flex align-items-center justify-content-start px-4"
              >
                <span className="me-3">📗</span>
                Office 365
              </Button>
              
              <Button 
                color="secondary" 
                size="lg" 
                onClick={() => handleCalendarSelection('apple')}
                className="d-flex align-items-center justify-content-start px-4"
              >
                <span className="me-3">🍎</span>
                Apple Calendar (iCal)
              </Button>
              
              <Button 
                color="warning" 
                size="lg" 
                onClick={() => handleCalendarSelection('yahoo')}
                className="d-flex align-items-center justify-content-start px-4 text-white"
              >
                <span className="me-3">📨</span>
                Yahoo Calendar
              </Button>
              
              <hr className="my-3" />
              
              <Button 
                color="dark" 
                size="lg" 
                onClick={() => handleCalendarSelection('ics')}
                className="d-flex align-items-center justify-content-start px-4"
              >
                <span className="me-3">💾</span>
                {t("Descargar archivo .ics")}
                <small className="ms-auto opacity-75">{t("(Universal)")}</small>
              </Button>
            </div>
            
            <p className="text-muted mt-4 small">
              {t("El evento incluye recordatorios automáticos: 1 semana y 1 día antes del congreso.")}
            </p>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default Section;